import React, { useState, useEffect, useMemo } from 'react';
import { Table, Progress, ConfigProvider, Bubble } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';

import './index.scss';
import '../VideoProcess/index.scss';
import '../VideoCard/index.scss';
import '../VideoBox/index.scss';

import { getShowCount, transLanguage } from '@/pages/opinion/utils';
import moment from 'moment';
import RenderImg from '@/pages/opinion/components/RenderImg';
import AiImg from '@op/assets/svg/icon-ai.svg';
import BubbleCommon, {
  communityVideoEngagementRateText,
  engagementText,
  keywordDrillUrlVideoContentSummary,
  keywordDrillUrlVideoLiveStreamContentSummary,
} from '@/pages/opinion/components/BubbleCommon';
import { getFeedsComments } from '@/pages/opinion/services';
import { getCommunityVideoMaxMetric } from '@/pages/opinion/services/video';
import { channelInfo } from '@/pages/opinion/components/ChannelLabel';
import { duration } from '@/pages/opinion/utils/time';
import { getLocale, useHistory, useLocation } from 'umi';
import { getTeaLocale } from '@/pages/home/utils/lang';
import {
  getFeedsContentText,
  getVideoLabelEle,
  useLocalLanguage,
} from '@/pages/opinion/utils/languageValue';

import UserGuideCommunity from '@op/components/UserGuide/community';
import { NO_DATE } from '@/pages/opinion/constants';
import { keywordRenderFunc } from '@/pages/opinion/utils/text';
import videoCover from '@op/assets/svg/video-cover.svg';
import { goToFeedsReviewsDetail } from '@op/utils/url';
import { dealwithVideoJSON } from '@op/pages/community/utils';
import AiContentSummary from './AiContentSummary';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import HoverSortCommonSimple from '@op/components/HoverSortCommonSimple';
import { useGptSummaryList } from '../../hooks';
import useFullDialog from '@op/components/GPT/hooks/useFullDialog';
import CardCommon from '@op/components/CardCommon';
import { fullIcon } from '@op/constants/chart';
import { dealwith8BallParams, isSpecialEdition, translateVideoList } from '../utils';
import useLanguage from '@op/hooks/useLanguage';
import TagList from '../TagList';
import Search from '@op/components/FilterBoxCommon/filters/search';
import { DBO_V } from '@op/utils/format';

const { sortable, pageable, scrollable, autotip } = Table.addons;

export default function VideoTable(props: any) {
  const {
    gameInfo,
    wholeGameInfo,
    keepParams = {},
    language = 'origin',
    search = '',
    setTableParams,
    setLanguage,
    tableRef,
    showTagsOnly = false
  } = props;

  const { formatMessage, isEn } = useLanguage();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const by = searchParams.get('by');
  const order = searchParams.get('order');
  const history = useHistory();

  const norms = useMemo(() => {
    const baseNorms = [
      {
        key: 'Views',
        max: 'views',
        to: 'view',
        sortKey: 'view',
      },
      {
        key: 'Engagement',
        max: 'engagements',
        to: 'engagement',
        sortKey: 'engagement',
      },
      ...(isSpecialEdition(gameInfo)
        ? [{
          key: 'Engagement Rate',
          max: 'engagement_per_view',
          to: 'engagement_per_view',
          sortKey: 'engagement_per_view',
          isPercent: true,
          width: 230,
        },]
        : []),
      {
        key: 'Likes',
        max: 'likes',
        to: 'tweets_like',
        sortKey: 'tweets_like',
      },
      {
        key: 'Dislike',
        max: 'tweets_unlike',
        to: 'tweets_unlike',
        sortKey: 'tweets_unlike',
      },
      {
        key: 'Comments',
        max: 'comments',
        to: 'tweets_reply',
        sortKey: 'tweets_reply',
      },
      {
        key: 'Shares',
        max: 'shares',
        to: 'tweets_retweet',
        sortKey: 'share',
      },
      {
        key: 'Followers/Subscribers',
        max: 'followers',
        to: 'subscribe',
        sortKey: 'follower_number',
        width: 230,
      },
    ];
    return baseNorms;
  }, [gameInfo]);
  const sortKey = norms.filter((val) => val?.sortKey === by)[0]?.key || 'Views';
  const [sorts, setSorts] = useState<any>([
    {
      by: sortKey,
      order: order || 'desc',
    },
  ]);
  const [searchTags, setSearchTags] = useState('');

  /**
   * 列表排序
   * SortItem值有：date(comment_time)，view(观看数)，engagement(互动数)，like(点赞数)，comment(评论数),follower(订阅关注数)
   * SortModel：desc,asc
   */
  const sortMap: any = {
    'Video (Sort by Date)': 'comment_time',
    ...norms
      .filter((item) => Boolean(item.sortKey))
      .reduce((obj, curItem) => {
        const curObj: any = { ...obj };
        curObj[curItem.key] = curItem.sortKey;
        return curObj;
      }, {}),
  };
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [data, setData] = useState<any>({
    list: [],
    statistics: {},
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!keepParams.edition_unified_id) return;
    setLoading(true);
    setData({ ...data, list: [], statistics: {} });
    let sort_item = 'comment_time';
    let sort_model = 'desc';
    if (sorts?.[0]) {
      const { by, order } = sorts[0];
      sort_item = sortMap[by];
      sort_model = order;
    }

    let videoTag: string[] = [];
    if (searchTags) {
      videoTag = searchTags
        .split(/;|；/)
        .map(tag => tag.trim())
        .filter(tag => tag);
    }

    const params = {
      ...keepParams,
      page: pageIndex,
      page_size: pageSize,
      search_content: search || '',
      search_video_tags: videoTag || [],
      only_video_tag_comments: showTagsOnly,
      sort_item,
      sort_model,
    };

    Promise.all([
      getCommunityVideoMaxMetric(params),
      getFeedsComments(dealwith8BallParams(params)),
    ]).then(([totalInfo, tableList]) => {
      if (tableList) {
        if (data) {
          const newData = Object.assign({}, tableList);

          if (!Array.isArray(newData.list)) newData.list = [];
          if (totalInfo.length > 0) newData.statistics = totalInfo[0] || {};
          setData({ ...newData });

          setTableParams({
            sort_item,
            sort_model,
            total: newData.total ?? 0,
            search_content: search || '',
          });
        } else {
          setData({
            list: [],
            statistics: {},
          });
          setTableParams({
            sort_item,
            sort_model,
            total: 0,
            search_content: search || '',
          });
        }
        setError(false);
      } else {
        setData({
          list: [],
          statistics: {},
        });
        setTableParams({
          sort_item,
          sort_model,
          total: 0,
          search_content: search || '',
        });
        setError(true);
      }
      setLoading(false);
    });
  }, [keepParams, pageIndex, pageSize, sorts, search, searchTags, showTagsOnly]);

  useEffect(() => {
    setPageIndex(1);
    setPageSize(50);
  }, [keepParams, sorts, search]);

  function Process(props: { biggest: any; count: any }) {
    const { biggest, count } = props;

    let width = 0;

    if (!isNaN(biggest) && !isNaN(count) && biggest > 0) {
      width = count / biggest;
      if (width > 1) width = 1;
    }

    return (
      <Progress
        className="dbo-video-progress"
        percent={width * 100}
        strokeWidth={6}
        theme={'default'}
      />
    );
  }

  const renderNorm = (
    {
      to,
      max,
      isPercent,
    }: { key: string; max: string; to: string; sortKey: string; isPercent?: boolean },
    records: { [x: string]: any },
  ) => {
    const rawValue = records[to];
    let displayValue: string | number = '-';
    if (rawValue && rawValue !== -1) {
      if (isPercent) {
        displayValue = DBO_V.ratio_min(rawValue);
      } else {
        displayValue = getShowCount(rawValue);
      }
    }

    return (
      <div className="community-render-norm">
        <p>{displayValue}</p>
        <Process
          biggest={data.statistics?.[`max_${max}`]}
          count={rawValue === -1 ? 0 : rawValue}
        ></Process>
      </div>
    );
  };

  const renderContent = (records: any) => {
    return keywordRenderFunc({
      text: getFeedsContentText(records, language),
      keyword: search,
    });
  };

  const openVideoPage = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    comment_url: string | URL | undefined,
  ) => {
    if (!comment_url) return;
    window.open(comment_url);
    event.stopPropagation();
  };

  const renderDuration = (durationStr: string) => {
    let curDurationStr = durationStr;
    if (/^(\d+)s*$/i.test(durationStr)) {
      curDurationStr = duration(
        Number(String(durationStr).replace(/s/gi, '')),
        '',
      );
    }
    return curDurationStr;
  };

  const renderVideo = (records: any) => {
    if (!records) return null;
    const channel = channelInfo(records.channel_name);
    const { img } = channel;
    const videoInfo = { cover_image: '', duration: '', url: '' };

    const obj = dealwithVideoJSON(records.video_detail);
    Object.assign(videoInfo, obj);

    const getContentText = () => {
      const value = getFeedsContentText(records, language);

      return value.length > 80 ? (
        <div
          className="dbo-scroll-bubble-scroll"
          style={{ whiteSpace: 'pre-wrap' }}
        >
          {value}
        </div>
      ) : null;
    };

    return (
      <a
        href={records.content_url}
        onClick={(e) => e.preventDefault()}
        className="dbo-videobox-wrapper"
      >
        <div className="dbo-videobox">
          <div className="dbo-videobox-coverbox">
            <img
              src={wholeGameInfo?.entity_image}
              className="dbo-videobox-cover"
              style={{
                zIndex: 1,
                position: 'absolute',
              }}
            ></img>

            <RenderImg
              className="dbo-videobox-cover"
              coverImage={videoInfo.cover_image}
              defaultImage={videoCover}
              aigc={records?.aigc === 1}
              style={{
                zIndex: 2,
                position: 'relative',
              }}
            ></RenderImg>

            {videoInfo.duration && (
              <span className="dbo-videobox-time">
                {renderDuration(videoInfo.duration)}
              </span>
            )}
          </div>
          <div className="dbo-videobox-infobox">
            <Bubble
              dark
              placement="top"
              content={getContentText()}
              overlayClassName="dbo-scroll-bubble"
            >
              <span
                className="dbo-videobox-title"
                onClick={(e) => {
                  e.stopPropagation();
                  goToFeedsReviewsDetail(history, records, { language });
                  e.preventDefault();
                }}
              >
                {renderContent(records)}
              </span>
            </Bubble>

            <Bubble
              dark
              placement="top"
              content={formatMessage({ id: 'Go to original post' })}
            >
              <div
                className="dbo-videobox-share"
                onClick={(event) => {
                  event.stopPropagation();
                  openVideoPage(event, records.content_url);
                  event.preventDefault();
                }}
              >
                <SvgIcon
                  type="other-share-icon"
                  className="dbo-feeds-btn-icon"
                />
              </div>
            </Bubble>
            <div className="dbo-videobox-source">
              <img className="dbo-videobox-source-icon" src={img} />
              <span className="dbo-videobox-text">{records.reviewer}</span>
            </div>
            <div className="dbo-videobox-des">
              <span className="dbo-videobox-text">
                {moment(records.comment_time).format('YYYY-MM-DD HH:mm')}
              </span>
              <span className="dbo-videobox-text">
                {transLanguage(records.language)}
              </span>
            </div>
          </div>
        </div>
      </a>
    );
  };

  const getHeader = (key: string) => {
    if (key === 'Engagement')
      return (
        <BubbleCommon tipsMap={engagementText}>
          {formatMessage({ id: 'Engagement' })}
        </BubbleCommon>
      );

    if (key === 'Engagement Rate')
      return (
        <BubbleCommon tipsMap={communityVideoEngagementRateText}>
          {formatMessage({ id: 'Engagement Rate' })}
        </BubbleCommon>
      );

    return formatMessage({ id: key });
  };

  const { language: newLanguage } = useLocalLanguage({
    languageType: language,
  });

  const { gptSummaryList: gptSubTitleSummaryList } = useGptSummaryList({
    data,
    mediaType: 'subtitle',
    gameInfo,
    keepParams
  });

  const { gptSummaryList: gptDanmakuSummaryList } = useGptSummaryList({
    data,
    mediaType: 'danmaku',
    gameInfo,
    keepParams
  });

  const [descriptionContent, setDescriptionContent] = useState<{
    summary: string;
    renderHtml: string;
  }>();

  const { FullDialog, openFullDialog } = useFullDialog({
    fullTitle: formatMessage({ id: 'Description' }),
  });

  const columns = useMemo(() => {
    const columns: any = [
      {
        key: 'Video (Sort by Date)',
        header: (
          <div className="dbo-table-header-reviews">
            <HoverSortCommonSimple
              sort={sorts[0]}
              sortChange={(sortItem?: {
                by: string;
                order: 'asc' | 'desc';
              }) => {
                sortItem && setSorts([sortItem]);
              }}
            >
              {formatMessage({ id: 'Video (Sort by Date)' })}
            </HoverSortCommonSimple>
            <SingleTranslateSelect
              defaultValue={language}
              onChange={(_key: string, value: string) => {
                setLanguage?.(value);
              }}
            />
          </div>
        ),
        width: 530,
        render: (records: any) => renderVideo(records),
        fixed: 'left',
      },
    ];
    norms.forEach((item) => {
      columns.push({
        key: item.key,
        header: getHeader(item.key),
        width: item?.width || 190,
        render: (records: any) => {
          return renderNorm(item, records);
        },
      });
    });
    // 产品： 不需要了，都可以统一按照最新的规范 使用AI标签列来展示就好
    // if (wholeGameInfo.has_video_ext_info) {
    //   columns.push(
    //     {
    //       key: 'Selling Points',
    //       width: 150,
    //       header: formatMessage({ id: 'Selling Points' }),
    //       render: (records: any) => {
    //         return translateVideoList(
    //           { records, gptSubTitleSummaryList },
    //           false,
    //           isEn,
    //         );
    //       },
    //     },
    //     {
    //       key: 'Video Category',
    //       header: formatMessage({ id: 'Video Category' }),
    //       width: 150,
    //       render: (records: any) => {
    //         return translateVideoList(
    //           { records, gptSubTitleSummaryList },
    //           true,
    //           isEn,
    //         );
    //       },
    //     },
    //   );
    // }

    if (keepParams?.need_video_tags) {
      columns.push(
        {
          key: 'AI Tags',
          header: (
            <div className="dbo-table-search-ai-tags">
              <div className="dbo-inlineflex">
                <img src={AiImg} className="dbo-inlineflex-img" />
                {formatMessage({ id: 'AI Tags' })}
              </div>
              <Search
                defaultParams={{
                  placeholder: formatMessage({ id: 'Search AI Tags' }),
                  onSearch: (value: any) => { setSearchTags(value.trim()) },
                  classNam: 'dbo-video-search',
                  onClear: () => { setSearchTags('') },
                  defaultValue: searchTags
                }}
              />
            </div>
          ),
          width: 500,
          render: (records: any) => {
            const { video_tags, comment_uin } = records || {};
            if (!video_tags) return '-';

            try {
              const tagList = JSON.parse(video_tags);
              return <TagList list={tagList} key={comment_uin} />;
            } catch (err) {
              console.error('Invalid JSON in video_tags:', err);
              return '-';
            }
          },
        },
      );
    }


    columns.push(
      {
        key: 'AI Content Summary',
        header: (
          <BubbleCommon
            showIcon={false}
            placement="top"
            tipsMap={keywordDrillUrlVideoContentSummary()}
          >
            <div className="dbo-inlineflex">
              <img src={AiImg} className="dbo-inlineflex-img" />
              {formatMessage({ id: 'AI Content Summary' })}
            </div>
          </BubbleCommon>
        ),
        width: 520,
        render: (records: any) => {
          const key = records.comment_uin;
          const hasVideoSummary = records?.video_summary_en || records?.video_summary_zh;
          const summary = gptSubTitleSummaryList?.[key];
          const videoSummary = {
            summary_en: records?.video_summary_en || '',
            summary_cn: records?.video_summary_zh || '',
            summary_status: "success"
          };
          return (
            <AiContentSummary
              gameInfo={gameInfo}
              gptSummary={hasVideoSummary ? videoSummary : summary}
              data={records}
              mediaType="subtitle"
              key={key}
            ></AiContentSummary>
          );
        },
      },
      {
        key: 'AI Live Stream Comments Summary',
        header: (
          <BubbleCommon
            showIcon={false}
            placement="top"
            tipsMap={keywordDrillUrlVideoLiveStreamContentSummary()}
          >
            <div className="dbo-inlineflex">
              <img src={AiImg} className="dbo-inlineflex-img" />
              {formatMessage({ id: 'AI Live Stream Comments Summary' })}
            </div>
          </BubbleCommon>
        ),
        width: 520,
        render: (records: any) => {
          const key = records.comment_uin;
          const summary = gptDanmakuSummaryList?.[key];
          return (
            <AiContentSummary
              gameInfo={gameInfo}
              gptSummary={summary}
              data={records}
              mediaType="danmaku"
              key={key}
            ></AiContentSummary>
          );
        },
      },
    );

    columns.push({
      key: 'Description',
      header: getHeader('Description'),
      width: 488,
      render: (records: any) => {
        const content = records?.description;
        return (
          <CardCommon
            operations={!!content ? [fullIcon] : []}
            contentCard
            handleOperationsOnClick={() => {
              if (content) {
                setDescriptionContent({
                  renderHtml: `<p>${content}</p>`,
                  summary: content,
                });
                openFullDialog();
              }
            }}
          >
            <div className="text-show-info">{content}</div>
          </CardCommon>
        );
      },
    });

    if (gameInfo?.edition_unified_id === 'u477a9f3809a0b7af26ae6383139dd66a') {
      columns.push({
        key: 'video_label',
        header: (
          <div style={{ color: '#262832' }}>{formatMessage({ id: 'Tag' })}</div>
        ),
        width: 190,
        render: (records: any) => {
          return (
            <div className="video-label-box">
              {getVideoLabelEle(records, newLanguage)}
            </div>
          );
        },
      });
    }

    return columns;
  }, [
    newLanguage,
    language,
    formatMessage,
    data,
    gptSubTitleSummaryList,
    gptDanmakuSummaryList,
    norms,
    sorts,
    searchTags,
    keepParams,
    gameInfo,
  ]);

  const locale = getTeaLocale(getLocale());
  return (
    <>
      <ConfigProvider locale={locale}>
        <div style={{ position: 'relative' }}>
          <UserGuideCommunity></UserGuideCommunity>
          <div className="user-guide-video-mask"></div>
          <Table
            className="dbo-table-sticky dbo-table-sticky--withfilter dbo-video-table"
            verticalTop
            ref={tableRef}
            records={data.list}
            bordered={true}
            compact={true}
            recordKey={(record) => `${record?.comment_id}${record?.comment_uin}`}
            disableTextOverflow={true}
            columns={columns}
            rowClassName={() => 'dbo-video-table-row'}
            addons={[
              sortable({
                columns: [
                  ...norms
                    .filter((item) => Boolean(item.sortKey))
                    .map((item) => item.key),
                ],
                value: sorts,
                onChange: (value) => {
                  let newSorts = value;

                  if (value.length > 0) {
                    if (sorts.length > 0) {
                      if (value[0].by == sorts[0].by) {
                        value[0].order =
                          value[0].order === 'asc' ? 'asc' : 'desc';
                        newSorts = [value[0]];
                      } else {
                        value[0].order = 'desc';
                        newSorts = [value[0]];
                      }
                    } else {
                      value[0].order = 'desc';
                      newSorts = [value[0]];
                    }
                  } else {
                    if (sorts.length > 0) {
                      newSorts = [
                        {
                          by: sorts[0].by,
                          order: sorts[0].order === 'desc' ? 'asc' : 'desc',
                          sorter: undefined,
                        },
                      ];
                    }
                  }
                  console.log('newSorts', JSON.stringify(newSorts));
                  setSorts(newSorts);
                },
              }),
              scrollable({
                minWidth: 1280,
                onScrollBottom: () => console.log('到达底部'),
              }),
              pageable({
                recordCount: data.total || 0,
                pageIndex,
                pageSize,
                onPagingChange: (query) => {
                  query.pageIndex && setPageIndex(query.pageIndex);
                  query.pageSize && setPageSize(query.pageSize);
                },
              }),
              autotip({
                isLoading: loading,
                emptyText: formatMessage({
                  id: error ? NO_DATE.ERROR : NO_DATE.SELECTION,
                }),
                className: 'tableTip',
              }),
            ]}
          />
        </div>
      </ConfigProvider>
      <FullDialog {...(descriptionContent || {})}></FullDialog>
    </>
  );
}
